import {
  buildVolcoraThermalReceiptOps,
  saleHasCashPayment
} from '@/utils/thermalReceiptVolcora'
import { buildVolcoraThermalTabStatementOps } from '@/utils/thermalReceiptVolcoraTab'
import {
  pickBusinessLogoUrl,
  refreshBusinessInfoForThermal,
  resolveThermalLogoBmp
} from '@/utils/volcoraReceiptLogoBmp'
import { toPlainForIpc } from '@/utils/thermalIpcPlain.js'

export const RECEIPT_PRINTER_SETTINGS_KEY = 'receipt-printer-settings'

/** @typedef {'volcora_sdk'|'windows_escpos'} ThermalDriverMode */

/**
 * Ensure business info (including logo URLs) is loaded before thermal print.
 * @param {string} [logoUrlFallback]
 */
export async function resolveBusinessInfoForPrint (logoUrlFallback = null) {
  let info = await refreshBusinessInfoForThermal()
  if (!pickBusinessLogoUrl(info) && logoUrlFallback) {
    info = { ...(info || {}), logo_url: logoUrlFallback }
  }
  return info
}

export function defaultReceiptPrinterSettings () {
  return {
    enabled: false,
    driverMode: 'volcora_sdk',
    usbPortName: 'SP-USB1',
    windowsPrinterName: '',
    paperWidthMm: 80,
    cutType: 1,
    feedLinesBeforeCut: 4,
    drawerEnabled: true,
    drawerAutoOpenCash: true,
    drawerIndex: 1,
    drawerTime1: 50,
    drawerTime2: 50
  }
}

export async function loadReceiptPrinterSettings () {
  const def = defaultReceiptPrinterSettings()
  const api = typeof window !== 'undefined' ? window.electronAPI : null
  if (!api?.readFile) {
    return { ...def }
  }
  const r = await api.readFile(RECEIPT_PRINTER_SETTINGS_KEY)
  if (!r?.success || !r.data) {
    return { ...def }
  }
  try {
    const parsed = JSON.parse(r.data)
    return { ...def, ...parsed }
  } catch {
    return { ...def }
  }
}

export async function saveReceiptPrinterSettings (data) {
  const api = typeof window !== 'undefined' ? window.electronAPI : null
  if (!api?.writeFile) {
    return { success: false, error: 'storage_unavailable' }
  }
  const payload = JSON.stringify({ ...defaultReceiptPrinterSettings(), ...data }, null, 0)
  return api.writeFile(RECEIPT_PRINTER_SETTINGS_KEY, payload)
}

/** @param {ReturnType<typeof defaultReceiptPrinterSettings>} settings */
export function buildThermalIpcPayload (settings, extra = {}) {
  return {
    driverMode: settings.driverMode === 'windows_escpos' ? 'windows_escpos' : 'volcora_sdk',
    usbPortName: settings.usbPortName,
    windowsPrinterName: settings.windowsPrinterName,
    cutType: settings.cutType,
    feedLinesBeforeCut: settings.feedLinesBeforeCut,
    drawerIndex: settings.drawerIndex,
    time1: settings.drawerTime1,
    time2: settings.drawerTime2,
    ...extra
  }
}

export function isThermalSupported () {
  return typeof window !== 'undefined' && !!window.electronAPI?.thermalSupported
}

export function isThermalCashDrawerSupported () {
  return isThermalSupported()
}

/** @deprecated use isThermalCashDrawerSupported */
export function isVolcoraCashDrawerSupported () {
  return isThermalCashDrawerSupported()
}

/** @param {(k: string) => string} t */
export function thermalLabelMap (t) {
  return volcoraThermalLabelMap(t)
}

/** @param {(k: string) => string} t */
export function volcoraThermalLabelMap (t) {
  return {
    phone: t('settings.receiptPrinterLblPhone'),
    taxId: t('settings.receiptPrinterLblTaxId'),
    date: t('settings.receiptPrinterLblDate'),
    sale: t('settings.receiptPrinterLblSale'),
    saleSource: t('sales.saleReceiptSaleSource'),
    saleSourceTerminalFeed: t('sales.saleSourceTerminalFeed'),
    cashier: t('settings.receiptPrinterLblCashier'),
    customer: t('settings.receiptPrinterLblCustomer'),
    walkInCustomer: t('sales.saleReceiptWalkIn'),
    unknownProduct: t('settings.receiptPrinterLblUnknownProduct'),
    lineTotal: t('settings.receiptPrinterLblLineTotal'),
    subtotal: t('settings.receiptPrinterLblSubtotal'),
    discount: t('settings.receiptPrinterLblDiscount'),
    afterDiscount: t('settings.receiptPrinterLblAfterDiscount'),
    tax: t('settings.receiptPrinterLblTax'),
    tip: t('settings.receiptPrinterLblTip'),
    fee: t('settings.receiptPrinterLblFee'),
    pennyRounding: t('sales.saleReceiptPennyRounding'),
    cashTotal: t('sales.saleReceiptCashTotal'),
    total: t('settings.receiptPrinterLblTotal'),
    payments: t('settings.receiptPrinterLblPayments'),
    received: t('settings.receiptPrinterLblReceived'),
    change: t('settings.receiptPrinterLblChange'),
    paymentApproved: t('settings.receiptPrinterLblPaymentApproved'),
    paymentDeclined: t('settings.receiptPrinterLblPaymentDeclined'),
    authShort: t('settings.receiptPrinterLblAuthShort'),
    refShort: t('settings.receiptPrinterLblRefShort'),
    entryModeChip: t('settings.receiptPrinterLblEntryModeChip'),
    entryModeContactless: t('settings.receiptPrinterLblEntryModeContactless'),
    entryModeSwipe: t('settings.receiptPrinterLblEntryModeSwipe'),
    entryModeManual: t('settings.receiptPrinterLblEntryModeManual'),
    entryModeKeyed: t('settings.receiptPrinterLblEntryModeKeyed'),
    entryModeUnknown: t('settings.receiptPrinterLblEntryModeUnknown'),
    thankYou: t('settings.receiptPrinterLblThankYou'),
    testBanner: t('settings.receiptPrinterLblTestBanner'),
    testFooter: t('settings.receiptPrinterLblTestFooter'),
    portLabel: t('settings.receiptPrinterLblPortOk'),
    tabPaymentLine: t('sales.tabPaymentReceipt.lineItem', { id: '{id}' }),
    tabPaymentSummary: t('sales.tabPaymentReceipt.summaryTitle'),
    tabPaymentReceiptTitle: t('sales.tabPaymentReceipt.receiptTitle'),
    tabAccountSection: t('sales.tabPaymentReceipt.tabAccountSection'),
    thisPaymentSection: t('sales.tabPaymentReceipt.thisPaymentSection'),
    itemsChargedToTab: t('sales.tabPaymentReceipt.itemsChargedToTab'),
    tabChargesTotal: t('sales.tabPaymentReceipt.tabChargesTotal'),
    priorPayments: t('sales.tabPaymentReceipt.priorPayments'),
    amountPaid: t('sales.tabPaymentReceipt.amountPaid'),
    paidInFull: t('sales.tabPaymentReceipt.paidInFull'),
    credits: t('tabs.statement.credits'),
    creditBalance: t('tabs.statement.creditBalance'),
    creditDisplay: t('tabs.creditDisplay', { amount: '{amount}' }),
    tabPaymentAmount: t('sales.tabPaymentReceipt.paymentAmount'),
    tabNumber: t('tabs.tab'),
    tabOwner: t('tabs.statement.owner'),
    tabCurrentTotal: t('tabs.statement.netChargesLabel'),
    tabPaymentsBefore: t('tabs.statement.paymentsLabel'),
    tabBalanceBefore: t('sales.tabPaymentReceipt.balanceBefore'),
    tabThisPayment: t('sales.tabPaymentReceipt.thisPayment'),
    tabBalanceAfter: t('sales.tabPaymentReceipt.remainingBalance'),
    totalCharged: t('sales.tabPaymentReceipt.totalCharged'),
    startedOn: t('tabs.statement.startedOn'),
    chargedOn: t('tabs.statement.chargedOn'),
    undatedCharges: t('tabs.statement.undatedCharges')
  }
}

/**
 * @param {object} params
 * @param {object} params.sale
 * @param {(k: string) => string} params.t
 * @param {string} [params.logoUrlFallback]
 */
export async function printSaleViaThermal ({ sale, t, logoUrlFallback }) {
  const api = typeof window !== 'undefined' ? window.electronAPI : null
  if (!api?.thermalSupported) {
    return { used: false }
  }
  const settings = await loadReceiptPrinterSettings()
  if (!settings.enabled) {
    return { used: false }
  }
  const labels = volcoraThermalLabelMap(t)
  const resolvedInfo = await resolveBusinessInfoForPrint(logoUrlFallback)
  const logoBmpBase64 = await resolveThermalLogoBmp(
    resolvedInfo,
    settings.paperWidthMm,
    logoUrlFallback,
    { refreshOnMiss: false }
  )
  const operations = buildVolcoraThermalReceiptOps(sale, resolvedInfo, {
    paperWidthMm: settings.paperWidthMm,
    labels,
    publicSaleUrl: '',
    logoBmpBase64: logoBmpBase64 || undefined
  })
  const r = await api.thermalPrintReceipt(
    buildThermalIpcPayload(settings, { operations })
  )
  if (!r?.success) {
    return {
      used: true,
      ok: false,
      error: r?.error || 'print_failed',
      errorDetail: r?.errorDetail,
      code: r?.code
    }
  }
  return { used: true, ok: true }
}

/** @deprecated use printSaleViaThermal */
export const printSaleViaVolcoraThermal = printSaleViaThermal

/**
 * @param {ReturnType<typeof defaultReceiptPrinterSettings> | null | undefined} [settingsOverride]
 */
export async function openThermalCashDrawer (settingsOverride = null) {
  const api = typeof window !== 'undefined' ? window.electronAPI : null
  if (!api?.thermalSupported) {
    return { success: false, error: 'unsupported' }
  }
  const settings = settingsOverride || await loadReceiptPrinterSettings()
  if (!settings.enabled || !settings.drawerEnabled) {
    return { success: false, error: 'drawer_not_configured' }
  }
  try {
    return await api.thermalOpenDrawer(buildThermalIpcPayload(settings))
  } catch (e) {
    return { success: false, error: String(e?.message || e) }
  }
}

/** @deprecated use openThermalCashDrawer */
export const openVolcoraCashDrawer = openThermalCashDrawer

export async function tryAutoOpenThermalCashDrawer (sale) {
  if (!isThermalCashDrawerSupported()) {
    return
  }
  const settings = await loadReceiptPrinterSettings()
  if (!settings.enabled || !settings.drawerEnabled || !settings.drawerAutoOpenCash) {
    return
  }
  if (!saleHasCashPayment(sale)) {
    return
  }
  const r = await openThermalCashDrawer(settings)
  if (!r?.success) {
    console.warn('[Thermal] cash drawer:', r?.error, r?.errorDetail, r?.code)
  }
}

/** @deprecated use tryAutoOpenThermalCashDrawer */
export const tryAutoOpenVolcoraCashDrawer = tryAutoOpenThermalCashDrawer

export { saleHasCashPayment }

/** @param {(k: string) => string} t */
export function thermalTabStatementLabelMap (t) {
  return volcoraTabStatementLabelMap(t)
}

/** @param {(k: string) => string} t */
export function volcoraTabStatementLabelMap (t) {
  return {
    ...volcoraThermalLabelMap(t),
    tabStatement: t('tabs.statement.title'),
    owner: t('tabs.statement.owner'),
    charges: t('tabs.statement.charges'),
    credits: t('tabs.statement.credits'),
    payments: t('tabs.statement.paymentsLabel'),
    currentTotal: t('tabs.statement.netChargesLabel'),
    netCharges: t('tabs.statement.netChargesLabel'),
    balanceDue: t('tabs.statement.balanceDue'),
    balanceDueHint: t('tabs.statement.balanceDueHint'),
    creditBalance: t('tabs.statement.creditBalance'),
    creditDisplay: t('tabs.creditDisplay', { amount: '{amount}' }),
    noBalanceDue: t('tabs.statement.noBalanceDue'),
    startedOn: t('tabs.statement.startedOn'),
    chargedOn: t('tabs.statement.chargedOn'),
    undatedCharges: t('tabs.statement.undatedCharges'),
    statementPrintedOn: t('tabs.statement.printedOn')
  }
}

export async function printTabViaThermal ({ tab, summary, entries, participants, ownerLabel, t }) {
  const api = typeof window !== 'undefined' ? window.electronAPI : null
  if (!api?.thermalSupported) {
    return { used: false }
  }
  const settings = await loadReceiptPrinterSettings()
  if (!settings.enabled) {
    return { used: false }
  }
  const labels = volcoraTabStatementLabelMap(t)
  const resolvedInfo = await resolveBusinessInfoForPrint()
  const logoBmpBase64 = await resolveThermalLogoBmp(
    resolvedInfo,
    settings.paperWidthMm,
    null,
    { refreshOnMiss: false }
  )
  const operations = buildVolcoraThermalTabStatementOps(
    { tab, summary, entries, participants, businessInfo: resolvedInfo },
    {
      paperWidthMm: settings.paperWidthMm,
      labels,
      ownerLabel,
      logoBmpBase64: logoBmpBase64 || undefined,
      printedAt: new Date()
    }
  )
  const r = await api.thermalPrintReceipt(
    buildThermalIpcPayload(settings, { operations })
  )
  if (!r?.success) {
    return {
      used: true,
      ok: false,
      error: r?.error || 'print_failed',
      errorDetail: r?.errorDetail,
      code: r?.code
    }
  }
  return { used: true, ok: true }
}

/** @deprecated use printTabViaThermal */
export const printTabViaVolcoraThermal = printTabViaThermal

export async function runThermalDiag (action, settings, params = {}) {
  const api = typeof window !== 'undefined' ? window.electronAPI : null
  if (!api?.thermalDiagRun) {
    return { success: false, error: 'unsupported' }
  }
  const plainSettings = toPlainForIpc({
    ...defaultReceiptPrinterSettings(),
    ...(settings && typeof settings === 'object' ? settings : {})
  })
  return api.thermalDiagRun({
    action: String(action || ''),
    settings: plainSettings,
    params: toPlainForIpc(params && typeof params === 'object' ? params : {})
  })
}

export async function listThermalPrinters () {
  const api = typeof window !== 'undefined' ? window.electronAPI : null
  if (!api?.thermalListPrinters) {
    return { success: false, printers: [] }
  }
  return api.thermalListPrinters()
}

export async function loadThermalDriverInfo (settings) {
  const api = typeof window !== 'undefined' ? window.electronAPI : null
  if (!api?.thermalDriverInfo) {
    return { success: false, path: null, exists: false }
  }
  return api.thermalDriverInfo(buildThermalIpcPayload(settings || defaultReceiptPrinterSettings()))
}
